import CustomLoadingButton from 'Components/CustomLoadingButton'
import {
  useCreateSingleCodespaceMutation,
  useUpdateSingleCodespaceMutation,
} from 'Services/ControlDataManagement/Features/Codespace/codespaceApi'
import { SELECTED_CODESPACE_PARAMETER_KEY } from 'Services/ControlDataManagement/Features/Codespace/Hooks/useCodespacesWithParams'
import type { Codespace } from 'Services/ControlDataManagement/Features/Codespace/types'
import type { ControlDataServiceError } from 'Util/ApiClient'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamToUrl } from 'Util/url'
import { Box, FormControl, Grid, TextField } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

interface CodespaceFormProps {
  successBaseUrl: string
  submitLoading: boolean
  codespace?: Codespace
}

export default function CodespaceForm({
  codespace,
  successBaseUrl,
  submitLoading,
}: Readonly<CodespaceFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!codespace

  const [createSingleCodespace, { isLoading: createLoading }] =
    useCreateSingleCodespaceMutation()
  const [updateSingleCodespace, { isLoading: updateLoading }] =
    useUpdateSingleCodespaceMutation()

  const [codespaceDetails, setCodespaceDetails] = useState<Partial<Codespace>>(
    (isEdit && codespace) || {},
  )

  const handleChange = (value: string | undefined, field: keyof Codespace) => {
    if (value !== undefined) {
      setCodespaceDetails(
        (prevState) => ({ ...prevState, [field]: value }) as Codespace,
      )
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const codespaceData = {
      ...codespaceDetails,
    }

    if (codespace) {
      updateSingleCodespace({
        codespace_id: codespace.codespace_id,
        ...codespaceData,
      })
        .unwrap()
        .then(() => {
          SnackbarUtils.success(t`Codespace successfully updated.`)
          navigate(successBaseUrl)
        })
        .catch(({ data: errorResponse }: { data: ControlDataServiceError }) => {
          const { message } = errorResponse
          if (message) {
            SnackbarUtils.error(message)
          } else {
            SnackbarUtils.error(t`There was an error updating the codespace.`)
          }
        })
      return
    }

    createSingleCodespace(codespaceData)
      .unwrap()
      .then((codespaceResponse: Codespace) => {
        SnackbarUtils.success(t`Codespace successfully created.`)
        navigate(
          appendQueryParamToUrl(
            successBaseUrl,
            SELECTED_CODESPACE_PARAMETER_KEY,
            codespaceResponse.codespace_id,
          ),
        )
      })
      .catch(({ data: errorResponse }: { data: ControlDataServiceError }) => {
        const { message } = errorResponse
        if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error creating the codespace.`)
        }
      })
  }

  const isSubmitLoading = submitLoading || createLoading || updateLoading

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', mb: 4 }}>
            <FormControl sx={{ minWidth: 240, mr: 3 }}>
              <TextField
                name="codespace_id"
                required
                label={t`Codespace identifier`}
                size="small"
                value={codespaceDetails?.codespace_id ?? ''}
                slotProps={{ htmlInput: { maxLength: 40 } }}
                onChange={(e) => handleChange(e.target.value, 'codespace_id')}
              />
            </FormControl>
          </Box>
          <Box>
            <FormControl sx={{ minWidth: 400, mr: 3 }} size="small">
              <TextField
                name="description"
                required
                label={t`Description`}
                size="small"
                value={codespaceDetails?.description ?? ''}
                onChange={(e) => handleChange(e.target.value, 'description')}
                multiline={true}
                rows={4}
              />
            </FormControl>
          </Box>
          <Box sx={{ py: 4 }}>
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Save codespace`}
            </CustomLoadingButton>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}
